import { createSlice } from '@reduxjs/toolkit';
import {
    addTaskListItemfileThunk,
    createTaskListItemCommentThunk,
    createTaskListItemThunk,
    createTaskListThunk,
    deleteBoardListThunk,
    deleteTaskListItemThunk,
    dragTaskListItemThunk,
    editBoardListThunk,
    editTaskListItemThunk,
    fetchAllUrgentTasksThunk,
    fetchAssignedTaskThunk,
    fetchClientBoardDetails,
    fetchClientTaskThunk,
    fetchDueTasksListThunk,
    fetchSingleTaskItemThunk,
    markTaskListItemCompleteThunk
} from '../thunks/task';
import { BoardObject, BoardTaskObject, TaskState } from '../../params/redux/task';

// Initial state with default values for authentication and user info
const initialState: TaskState = {
    dueTasks: [],
    tasks: null,
    selectedBoardList: null,
    isAddingTask: false,
    selectedBoardTask: null,
    selectedTab: 0,
    viewTaskItemDetail: false,
    isAssigned: true,
    loaders: {
        isLoading: false,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        isSearching: false,
        isInserting: false
    }
};

// Slice for app-related states and actions
const taskSlice = createSlice({
    // Slice name
    name: 'task',
    // inital state for the slice
    initialState,
    reducers: {
        settingSelectedBoardList: (state, action) => {
            state.selectedBoardList = action.payload;
        },
        settingAssignedTaskFlag: (state, action) => {
            state.isAssigned = action.payload;
        },
        settingSelectedTab: (state, action) => {
            state.selectedTab = action.payload;
        },
        settingSelectedBoardTaskDetail: (state, action) => {
            state.selectedBoardTask = action.payload;
        },
        settingAddingTask: (state, action) => {
            state.isAddingTask = action.payload;
        },
        settingViewTaskItemDetail: (state, action) => {
            state.viewTaskItemDetail = action.payload;
        },
        // Resets loading states related to app actions
        resettingTaskLoader: (state) => {
            state.loaders = initialState.loaders;
        },
        // Resets all app data to the initial state
        resettingTaskData: () => initialState
    },
    extraReducers: (builder) => {
        // Handle async actions for login
        builder
            .addCase(fetchClientTaskThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
                state.tasks = null;
            })
            .addCase(fetchClientTaskThunk.fulfilled, (state, action) => {
                state.tasks = action.payload as BoardObject;
            })
            .addCase(fetchClientTaskThunk.rejected, (state) => {
                state.tasks = null;
            });
        builder
            .addCase(createTaskListThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(createTaskListThunk.fulfilled, () => {
                //
            })
            .addCase(createTaskListThunk.rejected, () => {
                //
            });
        builder
            .addCase(createTaskListItemThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(createTaskListItemThunk.fulfilled, () => {
                //
            })
            .addCase(createTaskListItemThunk.rejected, () => {
                //
            });
        builder
            .addCase(dragTaskListItemThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(dragTaskListItemThunk.fulfilled, () => {
                //
            })
            .addCase(dragTaskListItemThunk.rejected, () => {
                //
            });
        builder
            .addCase(createTaskListItemCommentThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(createTaskListItemCommentThunk.fulfilled, () => {
                //
            })
            .addCase(createTaskListItemCommentThunk.rejected, () => {
                //
            });
        builder
            .addCase(addTaskListItemfileThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(addTaskListItemfileThunk.fulfilled, () => {
                //
            })
            .addCase(addTaskListItemfileThunk.rejected, () => {
                //
            });
        builder
            .addCase(fetchSingleTaskItemThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchSingleTaskItemThunk.fulfilled, (state, action) => {
                state.selectedBoardTask = action.payload as BoardTaskObject;
            })
            .addCase(fetchSingleTaskItemThunk.rejected, (state) => {
                state.selectedBoardTask = null;
                state.viewTaskItemDetail = false;
            });
        builder
            .addCase(deleteTaskListItemThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isDeleting = true;
            })
            .addCase(deleteTaskListItemThunk.fulfilled, () => {
                //
            })
            .addCase(deleteTaskListItemThunk.rejected, () => {
                //
            });
        builder
            .addCase(markTaskListItemCompleteThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(markTaskListItemCompleteThunk.fulfilled, () => {
                //
            })
            .addCase(markTaskListItemCompleteThunk.rejected, () => {
                //
            });
        builder
            .addCase(editTaskListItemThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(editTaskListItemThunk.fulfilled, () => {
                //
            })
            .addCase(editTaskListItemThunk.rejected, () => {
                //
            });
        builder
            .addCase(fetchDueTasksListThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchDueTasksListThunk.fulfilled, (state, action) => {
                state.dueTasks = action.payload as BoardTaskObject[];
            })
            .addCase(fetchDueTasksListThunk.rejected, (state) => {
                state.dueTasks = [];
            });
        builder
            .addCase(fetchAssignedTaskThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
                state.tasks = null;
            })
            .addCase(fetchAssignedTaskThunk.fulfilled, (state, action) => {
                state.tasks = action.payload as BoardObject;
            })
            .addCase(fetchAssignedTaskThunk.rejected, (state) => {
                state.tasks = null;
            });
        builder
            .addCase(editBoardListThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(editBoardListThunk.fulfilled, () => {
                //
            })
            .addCase(editBoardListThunk.rejected, () => {
                //
            });
        builder
            .addCase(deleteBoardListThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isDeleting = true;
            })
            .addCase(deleteBoardListThunk.fulfilled, () => {
                //
            })
            .addCase(deleteBoardListThunk.rejected, () => {
                //
            });
        builder
            .addCase(fetchAllUrgentTasksThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchAllUrgentTasksThunk.fulfilled, (state, action) => {
                state.dueTasks = action.payload as BoardTaskObject[];
            })
            .addCase(fetchAllUrgentTasksThunk.rejected, (state) => {
                state.dueTasks = [];
            });
        builder
            .addCase(fetchClientBoardDetails.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchClientBoardDetails.fulfilled, (state, action) => {
                state.tasks = action.payload as BoardObject;
            })
            .addCase(fetchClientBoardDetails.rejected, (state) => {
                state.tasks = null;
            });
    }
});

// Exporting actions for updating app state
export const {
    settingAssignedTaskFlag,
    settingSelectedBoardTaskDetail,
    settingViewTaskItemDetail,
    settingSelectedTab,
    settingAddingTask,
    settingSelectedBoardList,
    resettingTaskData,
    resettingTaskLoader
} = taskSlice.actions;

// Exporting the reducer to be used in the store
export default taskSlice.reducer;
